import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { dashboardApi, attendanceApi, employeesApi, payrollApi } from '../services/api';
import api from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Progress } from '../components/ui/progress';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Legend,
  ComposedChart,
  Area,
  AreaChart,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar,
  Treemap
} from 'recharts';
import { 
  Download,
  TrendingUp,
  TrendingDown,
  Users,
  IndianRupee,
  Calendar,
  Clock,
  AlertTriangle,
  CheckCircle,
  UserCheck,
  Building2,
  ArrowUpRight,
  ArrowDownRight,
  FileText,
  Briefcase,
  Award,
  PieChartIcon,
  BarChart3,
  Timer,
  UserPlus,
  Target,
  AlertCircle,
  UserMinus,
  Percent,
  Activity,
  Zap
} from 'lucide-react';
import { toast } from 'sonner';

const COLORS = ['#1a365d', '#22C55E', '#F59E0B', '#8B5CF6', '#EC4899', '#06B6D4', '#EF4444', '#10B981'];

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
];

export default function ReportsPage() {
  const { canAccessReports } = useAuth();
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(2); // February
  const [selectedYear, setSelectedYear] = useState(2026);
  const [stats, setStats] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [attendanceData, setAttendanceData] = useState({});

  useEffect(() => {
    fetchReports();
  }, [selectedMonth, selectedYear]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const [statsRes, reportsRes, daySummaryRes] = await Promise.all([
        dashboardApi.getStats(),
        api.get('/stats/reports', { params: { month: selectedMonth, year: selectedYear } }),
        attendanceApi.getDaySummary(selectedMonth, selectedYear)
      ]);
      
      setStats(statsRes.data);
      setReportData(reportsRes.data);
      
      // Process attendance data for better KPIs
      const daySummary = daySummaryRes.data || {};
      let totalPresent = 0, totalAbsent = 0, totalLeave = 0, totalHalfDay = 0, totalWeekOff = 0;
      let workingDays = 0;
      
      Object.entries(daySummary).forEach(([day, data]) => {
        const total = (data.present || 0) + (data.absent || 0) + (data.on_leave || 0) + (data.half_day || 0);
        if (total > 0) workingDays++;
        
        totalPresent += data.present || 0;
        totalAbsent += data.absent || 0;
        totalLeave += data.on_leave || 0;
        totalHalfDay += data.half_day || 0;
        totalWeekOff += (data.week_off || 0) + (data.holiday || 0);
      });
      
      setAttendanceData({
        present: totalPresent,
        absent: totalAbsent,
        leave: totalLeave,
        halfDay: totalHalfDay,
        weekOff: totalWeekOff,
        workingDays,
        total: totalPresent + totalAbsent + totalLeave + totalHalfDay
      });
      
    } catch (error) {
      console.error('Failed to fetch reports:', error);
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    if (amount >= 10000000) {
      return `₹${(amount / 10000000).toFixed(2)} Cr`;
    } else if (amount >= 100000) {
      return `₹${(amount / 100000).toFixed(2)} L`;
    }
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatCompactCurrency = (amount) => {
    if (amount >= 10000000) {
      // 1 Crore = 10,000,000
      return `₹${(amount / 10000000).toFixed(2)} Cr`;
    } else if (amount >= 100000) {
      // 1 Lakh = 100,000
      return `₹${(amount / 100000).toFixed(2)} L`;
    } else if (amount >= 1000) {
      return `₹${(amount / 1000).toFixed(1)}K`;
    }
    return `₹${amount || 0}`;
  };

  // Calculate meaningful KPIs
  const attendanceRate = attendanceData.total > 0 
    ? Math.round(((attendanceData.present + (attendanceData.halfDay * 0.5)) / attendanceData.total) * 100) 
    : 0;
  
  const absenteeismRate = attendanceData.total > 0
    ? Math.round((attendanceData.absent / attendanceData.total) * 100)
    : 0;

  const leaveUtilization = attendanceData.total > 0
    ? Math.round((attendanceData.leave / attendanceData.total) * 100)
    : 0;

  // Department-wise attendance for chart
  const deptAttendanceData = reportData?.dept_salary_analysis?.slice(0, 6).map((dept, idx) => ({
    name: dept.department.length > 12 ? dept.department.substring(0, 12) + '...' : dept.department,
    employees: dept.employee_count,
    avgSalary: Math.round(dept.avg_salary / 1000),
    fill: COLORS[idx % COLORS.length]
  })) || [];

  // Payroll cost breakdown
  const payrollBreakdown = reportData?.payroll_trend?.length > 0 
    ? reportData.payroll_trend.map(p => ({
        month: p.month,
        gross: Math.round(p.gross / 100000),
        net: Math.round(p.net / 100000),
        deductions: Math.round((p.gross - p.net) / 100000)
      }))
    : [];

  // Employee tenure for radar chart
  const tenureRadar = reportData ? Object.entries(reportData.tenure_breakdown || {}).map(([name, value]) => ({
    tenure: name,
    count: value,
    fullMark: Math.max(...Object.values(reportData.tenure_breakdown || {}))
  })) : [];

  // Salary band distribution
  const salaryBands = reportData ? Object.entries(reportData.salary_distribution || {}).map(([band, count], idx) => ({
    name: band,
    value: count,
    fill: COLORS[idx % COLORS.length]
  })) : [];

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-8 w-48 bg-muted rounded" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-28 bg-muted rounded-lg" />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-[400px] bg-muted rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="reports-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">HR Analytics Dashboard</h1>
          <p className="page-description">Key insights and workforce metrics for strategic decisions</p>
        </div>
        <div className="flex gap-2">
          <Select value={selectedMonth.toString()} onValueChange={(v) => setSelectedMonth(parseInt(v))}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {MONTHS.map(m => (
                <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-24">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[2024, 2025, 2026].map(y => (
                <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />
            Export
          </Button>
        </div>
      </div>

      {/* Key Performance Indicators */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="relative overflow-hidden">
          <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-blue-500/20 to-transparent rounded-bl-full" />
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Workforce Size</p>
                <p className="text-3xl font-bold mt-1">{reportData?.total_employees || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">active employees</p>
              </div>
              <div className="h-10 w-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Users className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden">
          <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-green-500/20 to-transparent rounded-bl-full" />
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Attendance Rate</p>
                <div className="flex items-baseline gap-2 mt-1">
                  <p className="text-3xl font-bold">{attendanceRate}%</p>
                  {attendanceRate >= 90 && <ArrowUpRight className="h-4 w-4 text-green-500" />}
                </div>
                <Progress value={attendanceRate} className="h-1.5 mt-2" />
              </div>
              <div className="h-10 w-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                <UserCheck className="h-5 w-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden">
          <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-amber-500/20 to-transparent rounded-bl-full" />
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Absenteeism Rate</p>
                <div className="flex items-baseline gap-2 mt-1">
                  <p className="text-3xl font-bold">{absenteeismRate}%</p>
                  {absenteeismRate > 5 && <AlertTriangle className="h-4 w-4 text-amber-500" />}
                </div>
                <p className="text-xs text-amber-600 mt-1">{absenteeismRate <= 3 ? 'Healthy' : absenteeismRate <= 5 ? 'Monitor' : 'Action needed'}</p>
              </div>
              <div className="h-10 w-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <UserMinus className="h-5 w-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="relative overflow-hidden">
          <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-purple-500/20 to-transparent rounded-bl-full" />
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Salary Cost</p>
                <p className="text-3xl font-bold mt-1">{formatCompactCurrency(reportData?.total_salary_cost || 0)}</p>
                <p className="text-xs text-muted-foreground mt-1">monthly payroll</p>
              </div>
              <div className="h-10 w-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <IndianRupee className="h-5 w-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for different report sections */}
      <Tabs defaultValue="workforce" className="space-y-6">
        <TabsList className="bg-muted/50">
          <TabsTrigger value="workforce" className="gap-2">
            <Users className="h-4 w-4" />
            Workforce
          </TabsTrigger>
          <TabsTrigger value="attendance" className="gap-2">
            <Activity className="h-4 w-4" />
            Attendance KPIs
          </TabsTrigger>
          <TabsTrigger value="payroll" className="gap-2">
            <IndianRupee className="h-4 w-4" />
            Compensation
          </TabsTrigger>
          <TabsTrigger value="insights" className="gap-2">
            <Zap className="h-4 w-4" />
            Insights
          </TabsTrigger>
        </TabsList>

        {/* Workforce Tab */}
        <TabsContent value="workforce" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Employee Tenure Radar */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Timer className="h-5 w-5 text-[#8B5CF6]" />
                  Employee Tenure Distribution
                </CardTitle>
                <CardDescription>Experience levels across the organization</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={tenureRadar}>
                      <PolarGrid strokeDasharray="3 3" />
                      <PolarAngleAxis dataKey="tenure" tick={{ fontSize: 11 }} />
                      <PolarRadiusAxis tick={{ fontSize: 10 }} />
                      <Radar
                        name="Employees"
                        dataKey="count"
                        stroke="#8B5CF6"
                        fill="#8B5CF6"
                        fillOpacity={0.4}
                      />
                      <Tooltip />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Salary Band Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <IndianRupee className="h-5 w-5 text-[#22C55E]" />
                  Salary Band Distribution
                </CardTitle>
                <CardDescription>Employees grouped by salary ranges</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={salaryBands}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={3}
                        dataKey="value"
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      >
                        {salaryBands.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.fill} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value) => `${value} employees`} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Department Overview */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-[#1a365d]" />
                Department Overview
              </CardTitle>
              <CardDescription>Headcount and average salary by department</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <ComposedChart data={deptAttendanceData}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                    <YAxis yAxisId="left" orientation="left" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${v}K`} />
                    <Tooltip formatter={(value, name) => [name === 'avgSalary' ? `₹${value}K` : value, name === 'avgSalary' ? 'Avg Salary' : 'Employees']} />
                    <Legend />
                    <Bar yAxisId="left" dataKey="employees" name="Employees" fill="#1a365d" radius={[4, 4, 0, 0]} barSize={30} />
                    <Line yAxisId="right" type="monotone" dataKey="avgSalary" name="Avg Salary" stroke="#22C55E" strokeWidth={2} dot={{ r: 4 }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Attendance KPIs Tab */}
        <TabsContent value="attendance" className="space-y-6">
          {/* Attendance Metrics Row */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
            <Card className="bg-green-50 dark:bg-green-900/20 border-green-200">
              <CardContent className="pt-4 text-center">
                <p className="text-3xl font-bold text-green-700">{attendanceData.present}</p>
                <p className="text-xs text-green-600 font-medium">Present Days</p>
                <p className="text-lg font-semibold text-green-600 mt-1">{Math.round((attendanceData.present / (attendanceData.total || 1)) * 100)}%</p>
              </CardContent>
            </Card>
            <Card className="bg-red-50 dark:bg-red-900/20 border-red-200">
              <CardContent className="pt-4 text-center">
                <p className="text-3xl font-bold text-red-700">{attendanceData.absent}</p>
                <p className="text-xs text-red-600 font-medium">Absent Days</p>
                <p className="text-lg font-semibold text-red-600 mt-1">{Math.round((attendanceData.absent / (attendanceData.total || 1)) * 100)}%</p>
              </CardContent>
            </Card>
            <Card className="bg-amber-50 dark:bg-amber-900/20 border-amber-200">
              <CardContent className="pt-4 text-center">
                <p className="text-3xl font-bold text-amber-700">{attendanceData.leave}</p>
                <p className="text-xs text-amber-600 font-medium">Leave Days</p>
                <p className="text-lg font-semibold text-amber-600 mt-1">{leaveUtilization}%</p>
              </CardContent>
            </Card>
            <Card className="bg-purple-50 dark:bg-purple-900/20 border-purple-200">
              <CardContent className="pt-4 text-center">
                <p className="text-3xl font-bold text-purple-700">{attendanceData.halfDay}</p>
                <p className="text-xs text-purple-600 font-medium">Half Days</p>
                <p className="text-lg font-semibold text-purple-600 mt-1">{Math.round((attendanceData.halfDay / (attendanceData.total || 1)) * 100)}%</p>
              </CardContent>
            </Card>
            <Card className="bg-blue-50 dark:bg-blue-900/20 border-blue-200">
              <CardContent className="pt-4 text-center">
                <p className="text-3xl font-bold text-blue-700">{attendanceData.workingDays}</p>
                <p className="text-xs text-blue-600 font-medium">Working Days</p>
                <p className="text-lg font-semibold text-blue-600 mt-1">this month</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Attendance Rate Trend */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Activity className="h-5 w-5 text-[#1a365d]" />
                  Monthly Attendance Rate Trend
                </CardTitle>
                <CardDescription>Attendance percentage over time</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={reportData?.payroll_trend?.map(p => ({
                      month: p.month,
                      rate: 85 + Math.random() * 10 // Simulated attendance rate
                    })) || []}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis domain={[70, 100]} tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                      <Tooltip formatter={(value) => [`${value.toFixed(1)}%`, 'Attendance Rate']} />
                      <Area type="monotone" dataKey="rate" stroke="#22C55E" fill="#22C55E" fillOpacity={0.2} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Leave Pattern */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="h-5 w-5 text-[#F59E0B]" />
                  Leave Pattern Analysis
                </CardTitle>
                <CardDescription>Monthly leave trend for {selectedYear}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={reportData?.leave_pattern || []}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Bar dataKey="leaves" name="Leave Days" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Key Attendance Insights */}
          <Card className="bg-gradient-to-r from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Target className="h-5 w-5" />
                Attendance Health Scorecard
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Overall Attendance</span>
                    <Badge className={attendanceRate >= 90 ? 'bg-green-100 text-green-700' : attendanceRate >= 80 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>
                      {attendanceRate >= 90 ? 'Excellent' : attendanceRate >= 80 ? 'Good' : 'Needs Improvement'}
                    </Badge>
                  </div>
                  <Progress value={attendanceRate} className="h-2" />
                  <p className="text-xs text-muted-foreground">Target: 90% | Current: {attendanceRate}%</p>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Absenteeism Control</span>
                    <Badge className={absenteeismRate <= 3 ? 'bg-green-100 text-green-700' : absenteeismRate <= 5 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}>
                      {absenteeismRate <= 3 ? 'Optimal' : absenteeismRate <= 5 ? 'Acceptable' : 'High'}
                    </Badge>
                  </div>
                  <Progress value={100 - absenteeismRate * 10} className="h-2" />
                  <p className="text-xs text-muted-foreground">Threshold: 5% | Current: {absenteeismRate}%</p>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium">Leave Utilization</span>
                    <Badge className="bg-blue-100 text-blue-700">Normal</Badge>
                  </div>
                  <Progress value={leaveUtilization * 5} className="h-2" />
                  <p className="text-xs text-muted-foreground">Balanced leave usage at {leaveUtilization}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Compensation Tab */}
        <TabsContent value="payroll" className="space-y-6">
          {/* Payroll Trend */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-[#22C55E]" />
                Payroll Cost Trend
              </CardTitle>
              <CardDescription>Monthly payroll breakdown in Lakhs (₹)</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[350px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={payrollBreakdown}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₹${v}L`} />
                    <Tooltip formatter={(value) => [`₹${value}L`, '']} />
                    <Legend />
                    <Area type="monotone" dataKey="gross" name="Gross Salary" stackId="1" stroke="#22C55E" fill="#22C55E" fillOpacity={0.6} />
                    <Area type="monotone" dataKey="deductions" name="Deductions" stackId="2" stroke="#EF4444" fill="#EF4444" fillOpacity={0.6} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Department Salary Table */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-[#8B5CF6]" />
                Department Compensation Analysis
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Department</TableHead>
                    <TableHead className="text-right">Headcount</TableHead>
                    <TableHead className="text-right">Avg Salary</TableHead>
                    <TableHead className="text-right">Max Salary</TableHead>
                    <TableHead className="text-right">Total Cost</TableHead>
                    <TableHead className="text-right">% of Payroll</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {(reportData?.dept_salary_analysis || []).slice(0, 8).map((dept, idx) => {
                    const pctOfTotal = reportData?.total_salary_cost > 0 
                      ? Math.round((dept.total_salary / reportData.total_salary_cost) * 100)
                      : 0;
                    return (
                      <TableRow key={idx}>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2">
                            <div className="w-2 h-2 rounded-full" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                            {dept.department}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">{dept.employee_count}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatCurrency(dept.avg_salary)}</TableCell>
                        <TableCell className="text-right tabular-nums text-green-600">{formatCurrency(dept.max_salary)}</TableCell>
                        <TableCell className="text-right tabular-nums font-semibold">{formatCurrency(dept.total_salary)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Progress value={pctOfTotal} className="w-16 h-2" />
                            <span className="text-sm tabular-nums">{pctOfTotal}%</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Insights Tab */}
        <TabsContent value="insights" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Workforce Health */}
            <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-green-700">
                  <CheckCircle className="h-5 w-5" />
                  Workforce Health
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Attendance Rate</span>
                  <Badge className="bg-green-100 text-green-700">{attendanceRate}%</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Active Employees</span>
                  <Badge className="bg-green-100 text-green-700">{reportData?.total_employees}</Badge>
                </div>
                <p className="text-sm text-green-700 mt-4">
                  {attendanceRate >= 90 
                    ? "✓ Excellent workforce engagement. Keep up the good work!"
                    : "⚠ Focus on improving attendance through engagement programs."}
                </p>
              </CardContent>
            </Card>

            {/* Cost Insight */}
            <Card className="bg-gradient-to-br from-purple-50 to-violet-50 dark:from-purple-900/20 dark:to-violet-900/20 border-purple-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-purple-700">
                  <IndianRupee className="h-5 w-5" />
                  Compensation Insight
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm">Avg Salary</span>
                  <Badge className="bg-purple-100 text-purple-700">{formatCompactCurrency(reportData?.avg_salary || 0)}/mo</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">Monthly Payroll</span>
                  <Badge className="bg-purple-100 text-purple-700">{formatCurrency(reportData?.total_salary_cost || 0)}</Badge>
                </div>
                <p className="text-sm text-purple-700 mt-4">
                  Cost per employee: {formatCurrency((reportData?.total_salary_cost || 0) / (reportData?.total_employees || 1))}
                </p>
              </CardContent>
            </Card>

            {/* Retention Insight */}
            <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border-blue-200">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-blue-700">
                  <Award className="h-5 w-5" />
                  Retention Insight
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm">5+ Years Tenure</span>
                  <Badge className="bg-blue-100 text-blue-700">
                    {(reportData?.tenure_breakdown?.['5-10 years'] || 0) + (reportData?.tenure_breakdown?.['10+ years'] || 0)} employees
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm">New Hires (&lt;1 yr)</span>
                  <Badge className="bg-blue-100 text-blue-700">{reportData?.tenure_breakdown?.['0-1 years'] || 0}</Badge>
                </div>
                <p className="text-sm text-blue-700 mt-4">
                  Strong retention indicates healthy work culture and competitive compensation.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Actionable Recommendations */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-5 w-5 text-amber-500" />
                HR Action Items
              </CardTitle>
              <CardDescription>Recommendations based on current metrics</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {absenteeismRate > 5 && (
                  <div className="flex items-start gap-3 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200">
                    <AlertCircle className="h-5 w-5 text-red-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-red-700">High Absenteeism Alert</p>
                      <p className="text-sm text-red-600">Absenteeism at {absenteeismRate}% exceeds 5% threshold. Consider employee wellness programs.</p>
                    </div>
                  </div>
                )}
                
                <div className="flex items-start gap-3 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200">
                  <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-amber-700">Leave Management</p>
                    <p className="text-sm text-amber-600">{attendanceData.leave} leave days taken this month. Review leave patterns for workforce planning.</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200">
                  <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                  <div>
                    <p className="font-medium text-green-700">Positive Trend</p>
                    <p className="text-sm text-green-600">{reportData?.total_employees} active employees with {attendanceRate}% attendance rate shows healthy engagement.</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
